import { Injectable, signal, inject, effect, computed } from '@angular/core';
import { esCreditoMetodo, resolverFormaPagoSat } from '../shared/formas-pago-sat';
import { TestigoConfig, AccesorioConfig, TESTIGOS_DEFAULT, ACCESORIOS_DEFAULT } from '../shared/recepcion-catalogo';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, of, tap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiResponse } from '../models/api-response.model';
import { AuthService } from '../core/services/auth.service';

/**
 * Leyenda al pie del PDF de cotización cuando el taller no configuró la suya.
 * Es el texto que la app usó siempre; se conserva para no cambiarle el documento
 * a los talleres que no toquen la configuración.
 */
export const MENSAJE_COTIZACION_DEFAULT =
  'Esta cotización tiene una vigencia de 15 días naturales a partir de su emisión. ' +
  'Los precios mostrados son finales; en caso de requerir factura, el IVA se calculará al momento de emitirla.';

export interface MetodoPagoConfig {
  id: string;
  nombre: string;
  requiere_referencia: boolean;
  icono?: string;
  activo: boolean;
  codigo_sat?: string;
}

export interface MarcaConfig {
  id: string;
  nombre: string;
  activa: boolean;
}

export interface NotaCredito {
  id: string;
  clienteId: string;
  clienteNombre: string;
  monto: number;
  folio: string;
  fecha: string;
  usada: boolean;
  ventaId?: string;
}

export interface GastoFijoCatalogo {
  id: string;
  nombre: string;
  categoria?: string;
  monto_estimado: number;
  activo: boolean;
  icono?: string;
}

/**
 * Item sugerido dentro de un punto de revisión. Al inyectar el template, cada
 * item se materializa como un OrdenItem en la OS (piezas/precio pre-llenados,
 * el mecánico puede ajustar). Campos mínimos — el resto del OrdenItem queda
 * en sus defaults.
 */
export interface TemplateItem {
  nombre: string;
  /** No. de parte de la refacción (opcional para conceptos de mano de obra). */
  noParte?: string;
  /** Marca de la refacción. */
  marca?: string;
  piezas?: number;
  /** Precio de entrada: costo de la pieza para el taller. */
  precioCompra?: number;
  /** Precio de salida: precio al cliente. */
  precioVenta?: number;
}

/**
 * Un punto del template es exactamente lo que es en la OS: un nombre
 * (ej. "Frenos") + una lista de items (balatas, discos, líquido…).
 */
export interface TemplatePunto {
  nombre: string;
  items: TemplateItem[];
}

/**
 * Template sugerido de puntos de revisión. Es un conjunto nombrado de puntos
 * (cada uno con sus items) que el módulo de Órdenes de Servicio puede inyectar
 * al crear/editar una orden.
 */
export interface TemplateRevision {
  id: string;
  nombre: string;
  puntos: TemplatePunto[];
}

/**
 * Normaliza un template guardado en cualquiera de las dos formas históricas:
 * - v1 antigua: `puntos: string[]` — sólo nombres, sin items.
 * - v2 actual: `puntos: TemplatePunto[]` — nombre + items.
 *
 * Se aplica al cargar configuración para que la UI siempre vea la forma v2.
 */
export function normalizeTemplate(t: any): TemplateRevision {
  return {
    id: t?.id || 'tpl_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8),
    nombre: t?.nombre || 'Template sin nombre',
    puntos: ((t?.puntos as any[]) || []).map(p => {
      if (typeof p === 'string') {
        // Forma vieja: nombre puro, sin items.
        return { nombre: p, items: [] };
      }
      return {
        nombre: p?.nombre || '',
        items: ((p?.items as any[]) || []).map(it => ({
          nombre: it?.nombre || '',
          noParte: it?.noParte || undefined,
          marca: it?.marca || undefined,
          piezas: typeof it?.piezas === 'number' ? it.piezas : undefined,
          precioCompra: typeof it?.precioCompra === 'number' ? it.precioCompra : undefined,
          precioVenta: typeof it?.precioVenta === 'number' ? it.precioVenta : undefined,
        })),
      };
    }),
  };
}

@Injectable({
  providedIn: 'root'
})
export class ConfiguracionService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly apiUrl = `${environment.apiUrl}/configuracion`;

  private defaultMetodos: MetodoPagoConfig[] = [
    { id: 'efectivo', nombre: 'Efectivo', requiere_referencia: false, icono: 'ri-money-dollar-circle-line', activo: true, codigo_sat: '01' },
    { id: 'tarjeta', nombre: 'Tarjeta', requiere_referencia: true, icono: 'ri-bank-card-line', activo: true, codigo_sat: '04' },
    { id: 'transferencia', nombre: 'Transferencia', requiere_referencia: true, icono: 'ri-exchange-line', activo: true, codigo_sat: '03' },
    { id: 'credito', nombre: 'Crédito', requiere_referencia: false, icono: 'ri-hand-coin-line', activo: true, codigo_sat: '99' }
  ];

  // Catálogo único de marcas (refacciones / proveedores). El backend lo persiste como
  // `marcas` en configuracion. Las marcas que muestra el proveedor y las que se
  // ofrecen al agregar piezas externas a una OS provienen de aquí.
  private defaultMarcas: MarcaConfig[] = [
    { id: 'bosch', nombre: 'Bosch', activa: true },
    { id: 'brembo', nombre: 'Brembo', activa: true },
    { id: 'castrol', nombre: 'Castrol', activa: true },
    { id: 'acdelco', nombre: 'ACDelco', activa: true },
    { id: 'michelin', nombre: 'Michelin', activa: true },
    { id: 'ngk', nombre: 'NGK', activa: true },
    { id: 'gates', nombre: 'Gates', activa: true },
    { id: 'dorman', nombre: 'Dorman', activa: true },
    { id: 'gonher', nombre: 'Gonher', activa: true },
    { id: 'lth', nombre: 'LTH', activa: true },
    { id: 'mobil', nombre: 'Mobil', activa: true },
    { id: 'moog', nombre: 'Moog', activa: true },
    { id: 'kyb', nombre: 'KYB', activa: true },
    { id: 'generica', nombre: 'Genérica', activa: true },
  ];

  metodosPago = signal<MetodoPagoConfig[]>(this.defaultMetodos);
  marcas = signal<MarcaConfig[]>(this.defaultMarcas);
  /** @deprecated alias retro-compatible — usa `marcas` */
  marcasRefaccion = this.marcas;
  notasCredito = signal<NotaCredito[]>([]);
  permisosModulos = signal<Record<string, string[]>>({});
  gastosFijosCatalogo = signal<GastoFijoCatalogo[]>([]);
  templatesRevision = signal<TemplateRevision[]>([]);
  /**
   * Leyenda al pie del PDF de cotización. Cada taller la redacta a su gusto desde
   * Configuración; vacío ⇒ el PDF usa su texto por defecto.
   */
  mensajeCotizacion = signal<string>('');
  /**
   * Garantía / términos que el taller imprime en la Nota de Servicio. La OS puede
   * traer su propio texto (`observaciones_nota`); éste es el que se usa si viene vacío.
   */
  terminosGarantia = signal<string>('');
  /** Indicadores del tablero y accesorios de la hoja de recepción (Configuración › Recepción). */
  testigosRecepcion = signal<TestigoConfig[]>(TESTIGOS_DEFAULT);
  accesoriosRecepcion = signal<AccesorioConfig[]>(ACCESORIOS_DEFAULT);
  testigosActivos = computed(() => this.testigosRecepcion().filter(t => t.activo));
  accesoriosActivos = computed(() => this.accesoriosRecepcion().filter(a => a.activo));
  /** true cuando ya llegó la configuración real del backend (no los defaults locales). */
  cargada = signal(false);

  /** Métodos con que se puede abonar a una cuenta por cobrar: activos y que no sean crédito. */
  metodosAbono = computed(() => this.metodosPago().filter(m => m.activo && !esCreditoMetodo(m)));

  /** Método y código SAT que viajan en el abono, a partir del id del método elegido. */
  pagoDeMetodo(id: string): { metodo: string; forma_pago_sat: string } | null {
    const m = this.metodosPago().find(x => x.id === id);
    if (!m) return null;
    return { metodo: m.nombre.trim().toUpperCase(), forma_pago_sat: resolverFormaPagoSat(m.id, [m]) };
  }

  /** Método de abono por defecto: el de efectivo (01) o el primero disponible. */
  metodoAbonoDefault(): string {
    const lista = this.metodosAbono();
    return (lista.find(m => resolverFormaPagoSat(m.id, [m]) === '01') ?? lista[0])?.id ?? '';
  }

  constructor() {
    effect(() => {
      const user = this.authService.currentUser();
      if (user?.user.grupo !== 'SUPER_ADMIN') {
        this.cargarConfiguracion();
      }
    });
  }

  cargarConfiguracion(): void {
    this.http.get<ApiResponse<any>>(this.apiUrl).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          if (res.data.metodos_pago) this.metodosPago.set(res.data.metodos_pago);
          // `marcas` viene del backend; si está vacío usamos defaults para que la UI no
          // arranque con un catálogo vacío en tenants nuevos.
          if (res.data.marcas && res.data.marcas.length > 0) this.marcas.set(res.data.marcas);
          if (res.data.notas_credito) this.notasCredito.set(res.data.notas_credito);
          if (res.data.permisos_modulos) this.permisosModulos.set(res.data.permisos_modulos);
          this.gastosFijosCatalogo.set(res.data.gastos_fijos_catalogo || []);
          this.templatesRevision.set((res.data.templates_revision || []).map(normalizeTemplate));
          this.mensajeCotizacion.set(res.data.mensaje_cotizacion || '');
          this.terminosGarantia.set(res.data.terminos_garantia || '');
          // Sin `recepcion` guardada (talleres anteriores a la opción) quedan los defaults.
          const recepcion = res.data.recepcion || {};
          this.testigosRecepcion.set(Array.isArray(recepcion.testigos) ? recepcion.testigos : TESTIGOS_DEFAULT);
          this.accesoriosRecepcion.set(Array.isArray(recepcion.accesorios) ? recepcion.accesorios : ACCESORIOS_DEFAULT);
          this.cargada.set(true);
        }
      },
      error: () => {
        console.warn('Usando configuración local por defecto');
      }
    });
  }

  saveConfig(payload: any): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(this.apiUrl, payload).pipe(
      tap(() => this.cargarConfiguracion())
    );
  }

  // ─── Catálogo de marcas de productos (endpoint dedicado, no toca el resto de la config) ───

  /** Lee el catálogo de marcas. Por defecto solo activas; pasa todas=true para administrar. */
  getMarcasProductos(todas = false): Observable<MarcaConfig[]> {
    const url = `${environment.apiUrl}/catalogos/marcas-productos${todas ? '?todas=true' : ''}`;
    return this.http.get<ApiResponse<{ marcas: MarcaConfig[] }>>(url).pipe(
      tap(res => { if (res.data?.marcas) this.marcas.set(res.data.marcas); }),
      map(res => res.data?.marcas ?? [])
    );
  }

  /** Reemplaza el catálogo completo de marcas. */
  guardarMarcasProductos(marcas: MarcaConfig[]): Observable<ApiResponse<{ marcas: MarcaConfig[] }>> {
    const url = `${environment.apiUrl}/catalogos/marcas-productos`;
    return this.http.post<ApiResponse<{ marcas: MarcaConfig[] }>>(url, { marcas }).pipe(
      tap(res => { if (res.data?.marcas) this.marcas.set(res.data.marcas); })
    );
  }

  /** Operación puntual sobre una marca (add/update/delete). */
  mutarMarcaProducto(accion: 'add' | 'update' | 'delete', marca: Partial<MarcaConfig>): Observable<ApiResponse<{ marcas: MarcaConfig[] }>> {
    const url = `${environment.apiUrl}/catalogos/marcas-productos`;
    return this.http.post<ApiResponse<{ marcas: MarcaConfig[] }>>(url, { accion, marca }).pipe(
      tap(res => { if (res.data?.marcas) this.marcas.set(res.data.marcas); })
    );
  }

  /**
   * Alta en línea desde un formulario de producto: crea la marca y devuelve la que
   * quedó en el catálogo. Si ya existía (409) devuelve la existente en lugar de
   * fallar, así el formulario la selecciona igual. `semilla` protege a los tenants
   * que aún ven las marcas por defecto: el backend la usa sólo si no tiene ninguna.
   */
  crearMarcaEnLinea(nombre: string): Observable<MarcaConfig> {
    const url = `${environment.apiUrl}/catalogos/marcas-productos`;
    const limpio = nombre.trim();
    const buscar = (lista: MarcaConfig[] | undefined) =>
      (lista ?? []).find(m => m.nombre.trim().toLowerCase() === limpio.toLowerCase());
    return this.http.post<ApiResponse<{ marcas: MarcaConfig[] }>>(url, {
      accion: 'add',
      marca: { nombre: limpio },
      semilla: this.marcas(),
    }).pipe(
      tap(res => { if (res.data?.marcas) this.marcas.set(res.data.marcas); }),
      map(res => buscar(res.data?.marcas) ?? { id: '', nombre: limpio, activa: true }),
      catchError(err => {
        const existente: MarcaConfig | undefined = err?.error?.data?.existente;
        if (err?.status === 409 && existente) {
          if (err.error.data.marcas) this.marcas.set(err.error.data.marcas);
          return of(existente);
        }
        return throwError(() => err);
      }),
    );
  }

  // El límite de crédito vive en el propio cliente (`limite_credito`) y se
  // administra desde el módulo de Clientes. El backend valida contra ese campo
  // al cobrar, así que no hay catálogo de límites en la configuración.

  crearNota(clienteId: string, clienteNombre: string, monto: number): NotaCredito {
    const nueva: NotaCredito = {
      id: Date.now().toString(),
      clienteId,
      clienteNombre,
      monto,
      folio: 'NC-' + Math.floor(1000 + Math.random() * 9000),
      fecha: new Date().toISOString(),
      usada: false
    };
    
    const nuevasNotas = [nueva, ...this.notasCredito()];
    this.saveConfig({ notas_credito: nuevasNotas }).subscribe();
    return nueva;
  }
}
